import { isAxiosError } from "axios";
import type { CloudDocumentBytes, CloudDocumentOptions } from "./Platform";
import type { CloudDocumentTransport } from "./cloudDocumentTransport";
export type { CloudDocumentTransport } from "./cloudDocumentTransport";
export type { CloudDocumentBytes, CloudDocumentOptions, CloudOpenProgress } from "./Platform";
interface WorkingSetLinkRow {
    sha256: string;
    name?: string;
    cloudDocId?: string;
    projectId?: string;
    updatedAt: string;
}

function waitWithSignal<T>(
    promise: Promise<T>,
    signal: AbortSignal | undefined,
    abort: () => Error,
): Promise<T> {
    if (!signal) return promise;
    if (signal.aborted) return Promise.reject(abort());
    return new Promise<T>((resolve, reject) => {
        const onAbort = () => reject(abort());
        signal.addEventListener("abort", onAbort, { once: true });
        promise.then(resolve, reject).finally(() => signal.removeEventListener("abort", onAbort));
    });
}

function deferred<T>(): {
    promise: Promise<T>;
    resolve(value: T | PromiseLike<T>): void;
    reject(reason?: unknown): void;
} {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
        resolve = res;
        reject = rej;
    });
    return { promise, resolve, reject };
}

/** Keep the complete source bounded before allocating the destination buffer. */
export const MAX_WHOLE_DOCUMENT_SOURCE_BYTES = 1024 * 1024 * 1024;

async function sha256Hex(bytes: Uint8Array): Promise<string> {
    const digest = await crypto.subtle.digest("SHA-256", bytes as unknown as BufferSource);
    return Array.from(new Uint8Array(digest))
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("");
}

const SHA256_PATTERN = /^[0-9a-f]{64}$/i;

/** "%PDF-" -- every real PDF starts with this signature. The spec tolerates leading
 *  garbage before it, but Primepoint's own pipeline never emits any; a bounded scan is
 *  still enough to catch a misdirected HTML/JSON error page served with a 200 status and
 *  a plausible Content-Length -- nothing else in this module would ever notice that. */
const PDF_SIGNATURE = [0x25, 0x50, 0x44, 0x46, 0x2d];
const PDF_SIGNATURE_SCAN_BYTES = 1024;

function looksLikePdf(bytes: Uint8Array): boolean {
    const scanLength = Math.min(bytes.byteLength, PDF_SIGNATURE_SCAN_BYTES);
    for (let start = 0; start <= scanLength - PDF_SIGNATURE.length; start++) {
        if (PDF_SIGNATURE.every((byte, offset) => bytes[start + offset] === byte)) return true;
    }
    return false;
}

/** No caller has ever needed a real network round trip longer than this between bytes. */
const DEFAULT_DOWNLOAD_TIMEOUT_MS = 30_000;

export interface VerifiedCachedDocument {
    bytes: Uint8Array;
    verifiedSha256: string;
}

export interface CloudSourceFile {
    sha256?: string;
    fileSize?: number;
}

interface CloudDocumentMetadata {
    id: string;
    name: string;
    fileId?: string;
    downloadUrl?: string;
    sha256?: string;
}

export interface CloudDocumentStoreDependencies extends CloudDocumentTransport {
    getCached(sha256: string): Promise<VerifiedCachedDocument | null>;
    /** Bytes a file-id-keyed cache (the tenant's file-id index, or the sync engine's source
     *  cache) holds for `fileId`; the first thing a warm open asks, before any network work.
     *  With `expectedSha256`, the sha the file's row names now, each cache answers only from
     *  a download made for that sha, and one that cannot say which sha its bytes were
     *  downloaded for is a miss (one file id can carry other bytes: a pinned provider
     *  document's row moves its sha to the newest revision). `load` takes such a hit as it
     *  is: a split sheet's row names its original upload's sha, never the bytes', so the
     *  bytes' own hash cannot stand in for that check. */
    getCachedByFileId(
        fileId: string,
        expectedSha256?: string,
    ): Promise<VerifiedCachedDocument | null>;
    /** Metadata-only resident source probe. It must not copy PDF bytes over IPC. */
    probeCachedByFileId?(fileId: string): Promise<string | null>;
    getSourceFile(fileId: string): Promise<CloudSourceFile | null>;
    /** `fileId`, when known, lands in the tenant's file-id index together with the bytes
     *  and `requestedSha256`, the sha the file's row named for this download (a split
     *  sheet's row names its original upload's, never the bytes'): the download is the only
     *  writer of that index, and the native side binds the pair to `nativeAuthGeneration`
     *  (the session the bytes were fetched under). */
    putCached(
        sha256: string,
        bytes: Uint8Array,
        nativeAuthGeneration: number,
        fileId?: string,
        resourceGeneration?: number,
        requestedSha256?: string,
    ): Promise<void>;
    hasCached(sha256: string): Promise<boolean>;
    getLinks(): Promise<WorkingSetLinkRow[]>;
    setLink(
        sha256: string,
        patch: { name?: string; cloudDocId?: string; projectId?: string },
        nativeAuthGeneration: number,
        resourceGeneration?: number,
    ): Promise<WorkingSetLinkRow | undefined>;
    digestSha256?(bytes: Uint8Array): Promise<string>;
    /** Overridable only for tests -- production always uses `DEFAULT_DOWNLOAD_TIMEOUT_MS`. */
    downloadTimeoutMs?: number;
}

export interface CloudDocumentStore {
    load(documentId: string, options?: CloudDocumentOptions): Promise<CloudDocumentBytes>;
    download(documentId: string, options?: CloudDocumentOptions): Promise<CloudDocumentBytes>;
    hasCached(sha256: string): Promise<boolean>;
    getCached(sha256: string, signal?: AbortSignal): Promise<VerifiedCachedDocument | null>;
    getCachedByFileId(fileId: string, signal?: AbortSignal): Promise<VerifiedCachedDocument | null>;
    probeCachedByFileId(fileId: string, signal?: AbortSignal): Promise<string | null>;
    getSourceFile(fileId: string, signal?: AbortSignal): Promise<CloudSourceFile | null>;
}

/** Native cache half supplied by a host adapter. Network metadata and transport stay
 * in the shared download primitive so every host follows the same validation, bounds,
 * cancellation, and in-flight fencing rules. */
export type NativeCloudDocumentDependencies = Pick<
    CloudDocumentStoreDependencies,
    | "getCached"
    | "getCachedByFileId"
    | "probeCachedByFileId"
    | "getSourceFile"
    | "putCached"
    | "hasCached"
    | "getLinks"
    | "setLink"
    | "digestSha256"
>;

export type CloudDocumentStoreErrorKind =
    | "offline"
    | "auth"
    | "notFound"
    | "server"
    | "cache"
    | "empty-body"
    | "not-pdf"
    | "timeout"
    | `http-${number}`;

export class CloudDocumentStoreError extends Error {
    readonly kind: CloudDocumentStoreErrorKind;
    readonly causeValue?: unknown;

    constructor(kind: CloudDocumentStoreErrorKind, message: string, cause?: unknown) {
        super(message);
        this.name = "CloudDocumentStoreError";
        this.kind = kind;
        this.causeValue = cause;
    }
}

function abortError(): Error {
    if (typeof DOMException !== "undefined") {
        return new DOMException("Cloud document open was cancelled", "AbortError");
    }
    const error = new Error("Cloud document open was cancelled");
    error.name = "AbortError";
    return error;
}

function throwIfAborted(signal?: AbortSignal): void {
    if (signal?.aborted) throw abortError();
}

/** Same "race a stage against a timer" shape as `chunkedFileTransfer.ts`'s own
 *  `waitForStage` -- a stalled connect or a read that stops delivering chunks (an
 *  expired SAS URL a misbehaving proxy holds open, a dropped network association, ...)
 *  used to hang the whole open in "loading" forever: no rejection, no error, no toast,
 *  indistinguishable from a click that silently did nothing. */
async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, stage: string): Promise<T> {
    const timeout = deferred<T>();
    const timer = globalThis.setTimeout(() => {
        timeout.reject(
            new CloudDocumentStoreError(
                "timeout",
                `Document download stopped while ${stage}. Try again.`,
            ),
        );
    }, timeoutMs);
    try {
        return await Promise.race([promise, timeout.promise]);
    } finally {
        globalThis.clearTimeout(timer);
    }
}

function objectValue(value: unknown): Record<string, unknown> | null {
    return typeof value === "object" && value !== null && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;
}

function nonEmptyString(value: unknown): string | undefined {
    return typeof value === "string" && value.trim().length > 0 ? value : undefined;
}

function normalizedSha256(value: unknown): string | undefined {
    return typeof value === "string" && SHA256_PATTERN.test(value)
        ? value.toLowerCase()
        : undefined;
}

function normalizedFileSize(value: unknown): number | undefined {
    return typeof value === "number" && Number.isSafeInteger(value) && value > 0
        ? value
        : undefined;
}

function documentMetadata(value: unknown): CloudDocumentMetadata | null {
    const document = objectValue(value);
    if (!document) return null;
    const file = objectValue(document.file);
    const fileId = nonEmptyString(file?.id);
    const id = nonEmptyString(document.id) ?? fileId;
    const name =
        nonEmptyString(file?.fileName) ??
        nonEmptyString(document.title) ??
        nonEmptyString(document.name);
    if (!id || !name) return null;
    const downloadUrl = nonEmptyString(file?.fileUrl);
    const sha256 = normalizedSha256(file?.sha256) ?? normalizedSha256(document.sha256);
    return {
        id,
        name,
        ...(fileId ? { fileId } : {}),
        ...(downloadUrl ? { downloadUrl } : {}),
        ...(sha256 ? { sha256 } : {}),
    };
}
function fileMetadata(
    value: unknown,
    expectedFileId: string,
): Partial<Pick<CloudDocumentMetadata, "name" | "downloadUrl" | "sha256">> | null {
    const file = objectValue(value);
    if (!file || nonEmptyString(file.id) !== expectedFileId) return null;
    const name = nonEmptyString(file.fileName);
    const downloadUrl = nonEmptyString(file.fileUrl);
    const sha256 = normalizedSha256(file.sha256);
    if (!downloadUrl && !name && !sha256) return null;
    return {
        ...(name ? { name } : {}),
        ...(downloadUrl ? { downloadUrl } : {}),
        ...(sha256 ? { sha256 } : {}),
    };
}

/** A caller-held download URL the store may fetch from (https, or loopback http for a
 *  local dev server, never one with credentials); `null` for anything else, so the
 *  caller falls back to a metadata answer instead of failing the open. */
function parseDownloadUrl(raw: string | undefined): URL | null {
    if (typeof raw !== "string" || raw.trim().length === 0) return null;
    let url: URL;
    try {
        url = new URL(raw);
    } catch {
        return null;
    }
    const loopback =
        url.protocol === "http:" &&
        (url.hostname === "localhost" || url.hostname === "127.0.0.1" || url.hostname === "[::1]");
    if ((url.protocol !== "https:" && !loopback) || url.username || url.password) {
        return null;
    }
    return url;
}

function validatedDownloadUrl(raw: string): URL {
    const url = parseDownloadUrl(raw);
    if (!url) {
        throw new CloudDocumentStoreError("server", "Primepoint returned an invalid document URL");
    }
    return url;
}

function contentLength(response: Response): number {
    const raw = response.headers.get("content-length");
    if (!raw || !/^\d+$/.test(raw)) {
        throw new CloudDocumentStoreError(
            "server",
            "Document download did not provide a byte length; Primepoint did not buffer it in memory",
        );
    }
    const length = Number(raw);
    if (length === 0) {
        throw new CloudDocumentStoreError("empty-body", "Document download returned an empty file");
    }
    if (!Number.isSafeInteger(length) || length <= 0) {
        throw new CloudDocumentStoreError(
            "server",
            "Document download returned an invalid byte length",
        );
    }
    if (length > MAX_WHOLE_DOCUMENT_SOURCE_BYTES) {
        throw new CloudDocumentStoreError(
            "server",
            "Document exceeds Primepoint's 1 GiB whole-document source limit",
        );
    }
    return length;
}

function latestLinkForDocument(
    links: readonly WorkingSetLinkRow[],
    documentId: string,
): WorkingSetLinkRow | undefined {
    let latest: WorkingSetLinkRow | undefined;
    for (const link of links) {
        if (link.cloudDocId !== documentId) continue;
        if (!latest || link.updatedAt > latest.updatedAt) latest = link;
    }
    return latest;
}

function metadataFailure(error: unknown): Error {
    if (
        error instanceof CloudDocumentStoreError ||
        (error instanceof Error && error.name === "AbortError")
    ) {
        return error;
    }
    if (isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 404) {
            return new CloudDocumentStoreError(
                "notFound",
                "This project document was not found. It may have been removed.",
                error,
            );
        }
        if (status === 401 || status === 403) {
            return new CloudDocumentStoreError(
                "auth",
                status === 403
                    ? "You do not have access to this project document."
                    : "Sign in to open this project document.",
                error,
            );
        }
        if (status === undefined) {
            return new CloudDocumentStoreError(
                "offline",
                "Primepoint Cloud is not reachable. Check your connection and retry.",
                error,
            );
        }
        // Every other status Primepoint's API can return (500, 502, 503, ...) used to fall
        // through to a plain, untyped `Error` below -- a caller checking `.kind` (the toast,
        // the side pane's ErrorLine) saw nothing to classify it by, only whatever axios's
        // own message happened to say.
        return new CloudDocumentStoreError(
            `http-${status}`,
            `Primepoint Cloud returned an error (HTTP ${status}). Try again.`,
            error,
        );
    }
    return error instanceof Error ? error : new Error(String(error));
}

export function createCloudDocumentStore(
    dependencies: CloudDocumentStoreDependencies,
): CloudDocumentStore {
    const digestSha256 = dependencies.digestSha256 ?? sha256Hex;
    const inFlightDownloads = new Map<string, Promise<CloudDocumentBytes>>();

    const assertEpoch = (epoch: number): void => {
        if (!dependencies.isEpochCurrent(epoch)) {
            throw new CloudDocumentStoreError(
                "auth",
                "Your Primepoint session changed. Try the request again.",
            );
        }
    };

    const loadMetadata = async (
        documentId: string,
        sourceFileId: string | undefined,
        signal: AbortSignal | undefined,
        epoch: number,
    ): Promise<CloudDocumentMetadata> => {
        if (sourceFileId) {
            let sourcePayload: unknown;
            try {
                sourcePayload = await dependencies.loadFileMetadata(sourceFileId, signal);
            } catch (error) {
                throw metadataFailure(error);
            }
            assertEpoch(epoch);
            const source = fileMetadata(sourcePayload, sourceFileId);
            if (!source?.downloadUrl) {
                throw new CloudDocumentStoreError(
                    "server",
                    `${source?.name ?? "This project file"} is not ready to open yet. It may still be processing.`,
                );
            }
            return {
                id: documentId,
                name: source.name ?? "Project document.pdf",
                fileId: sourceFileId,
                downloadUrl: source.downloadUrl,
                ...(source.sha256 ? { sha256: source.sha256 } : {}),
            };
        }

        let payload: unknown;
        try {
            payload = await dependencies.loadDocumentMetadata(documentId, signal);
        } catch (error) {
            throw metadataFailure(error);
        }
        assertEpoch(epoch);
        const metadata = documentMetadata(payload);
        if (!metadata || metadata.id !== documentId) {
            throw new CloudDocumentStoreError(
                "server",
                "Document metadata returned an invalid response",
            );
        }
        if (!metadata.downloadUrl) {
            throw new CloudDocumentStoreError(
                "server",
                "Document metadata has no downloadable PDF",
            );
        }
        return metadata;
    };

    const fetchDocument = async (
        downloadUrl: URL,
        options: CloudDocumentOptions,
        epoch: number,
        downloadTimeoutMs: number,
    ): Promise<Response> => {
        let response: Response;
        try {
            response = await withTimeout(
                dependencies.fetch(downloadUrl, {
                    method: "GET",
                    credentials: "omit",
                    headers: { Accept: "application/pdf" },
                    signal: options.signal,
                }),
                downloadTimeoutMs,
                "connecting to Primepoint Cloud",
            );
        } catch (error) {
            if (options.signal?.aborted) throw abortError();
            if (error instanceof CloudDocumentStoreError) throw error;
            throw new CloudDocumentStoreError(
                "offline",
                "Primepoint Cloud is not reachable. Check your connection and retry.",
                error,
            );
        }
        assertEpoch(epoch);
        return response;
    };

    /** The `load` result for a cache hit: progress reported as one complete step, the
     *  abort checked after it, the bytes under their verified sha. */
    const cachedResult = (
        cached: VerifiedCachedDocument,
        name: string,
        options: CloudDocumentOptions,
    ): CloudDocumentBytes => {
        options.onProgress?.({
            received: cached.bytes.byteLength,
            total: cached.bytes.byteLength,
        });
        throwIfAborted(options.signal);
        return {
            bytes: cached.bytes,
            sha256: cached.verifiedSha256,
            name,
            source: "cache",
            verifiedSha256: cached.verifiedSha256,
        };
    };

    const downloadOnce = async (
        documentId: string,
        options: CloudDocumentOptions = {},
    ): Promise<CloudDocumentBytes> => {
        throwIfAborted(options.signal);
        if (!nonEmptyString(documentId)) {
            throw new CloudDocumentStoreError("server", "Document id is required");
        }
        const epoch = dependencies.captureEpoch();
        const session = await waitWithSignal(
            dependencies.getAuthSession(),
            options.signal,
            abortError,
        );
        assertEpoch(epoch);
        if (
            !session ||
            typeof session.generation !== "number" ||
            !Number.isSafeInteger(session.generation)
        ) {
            throw new CloudDocumentStoreError("auth", "Sign in to open this project document.");
        }
        const nativeAuthGeneration = session.generation;
        // A caller that already holds the file's download URL (the section-group listing,
        // a citation's file record: every `ApiFile` carries `fileUrl`) starts the bytes at
        // once. The metadata GET that used to precede every download runs only for a
        // caller without a URL -- or once more, below, when the held URL has expired.
        const knownUrl = parseDownloadUrl(
            options.downloadUrl
                ? (dependencies.resolveDownloadUrl?.(options.downloadUrl) ?? options.downloadUrl)
                : undefined,
        );
        let metadata: CloudDocumentMetadata;
        if (knownUrl) {
            metadata = {
                id: documentId,
                name: options.expectedName ?? "Project document.pdf",
                ...(options.sourceFileId ? { fileId: options.sourceFileId } : {}),
                downloadUrl: knownUrl.href,
            };
        } else {
            metadata = await loadMetadata(documentId, options.sourceFileId, options.signal, epoch);
        }
        const name = options.expectedName ?? metadata.name;
        const metadataFileId = metadata.fileId ?? options.sourceFileId;
        // The sha this download is made for (`putCached`): the caller's row's, else the metadata's.
        const requestedSha256 = normalizedSha256(options.expectedSha256) ?? metadata.sha256;
        // A metadata answer can name a sha the cache already holds, or a file id whose bytes
        // the file-id caches hold. A hit answers without any write: the tenant's file-id
        // index learns a pair only from a download the native side verified itself.
        let cached: VerifiedCachedDocument | null = null;
        if (metadata.sha256) {
            try {
                cached = await dependencies.getCached(metadata.sha256);
            } catch {
                cached = null;
            }
        }
        if (!cached && metadataFileId) {
            try {
                const sourceCached = await dependencies.getCachedByFileId(
                    metadataFileId,
                    requestedSha256,
                );
                if (
                    sourceCached &&
                    (!metadata.sha256 || sourceCached.verifiedSha256 === metadata.sha256)
                ) {
                    cached = sourceCached;
                }
            } catch {
                cached = null;
            }
        }
        if (cached) return cachedResult(cached, name, options);
        const downloadTimeoutMs = dependencies.downloadTimeoutMs ?? DEFAULT_DOWNLOAD_TIMEOUT_MS;
        let response = await fetchDocument(
            validatedDownloadUrl(
                dependencies.resolveDownloadUrl?.(metadata.downloadUrl!) ?? metadata.downloadUrl!,
            ),
            options,
            epoch,
            downloadTimeoutMs,
        );
        if (knownUrl && (response.status === 401 || response.status === 403)) {
            // The held URL was a time-limited one from an old listing: ask Primepoint for a
            // fresh URL once, then retry the bytes.
            metadata = await loadMetadata(documentId, options.sourceFileId, options.signal, epoch);
            response = await fetchDocument(
                validatedDownloadUrl(
                    dependencies.resolveDownloadUrl?.(metadata.downloadUrl!) ??
                        metadata.downloadUrl!,
                ),
                options,
                epoch,
                downloadTimeoutMs,
            );
        }
        if (!response.ok) {
            throw new CloudDocumentStoreError(
                response.status === 401 || response.status === 403
                    ? "auth"
                    : `http-${response.status}`,
                `Document download failed (HTTP ${response.status})`,
            );
        }
        if (!response.body) {
            throw new CloudDocumentStoreError(
                "empty-body",
                "Document download returned no response body",
            );
        }

        const totalBytes = contentLength(response);
        const bytes = new Uint8Array(totalBytes);
        let receivedBytes = 0;
        options.onProgress?.({ received: 0, total: totalBytes });
        const reader = response.body.getReader();
        try {
            while (true) {
                const { done, value } = await withTimeout(
                    reader.read(),
                    downloadTimeoutMs,
                    `receiving ${Math.round((receivedBytes / totalBytes) * 100)}%`,
                );
                assertEpoch(epoch);
                throwIfAborted(options.signal);
                if (done) break;
                if (receivedBytes + value.byteLength > totalBytes) {
                    throw new CloudDocumentStoreError(
                        "server",
                        "Document download exceeded its declared byte length",
                    );
                }
                bytes.set(value, receivedBytes);
                receivedBytes += value.byteLength;
                options.onProgress?.({ received: receivedBytes, total: totalBytes });
            }
        } catch (error) {
            await reader.cancel().catch(() => undefined);
            throw error;
        } finally {
            reader.releaseLock();
        }
        if (receivedBytes !== totalBytes) {
            throw new CloudDocumentStoreError(
                "offline",
                "Document download ended before it was complete",
            );
        }
        if (!looksLikePdf(bytes)) {
            throw new CloudDocumentStoreError(
                "not-pdf",
                "Primepoint returned a file that is not a PDF. Try again, or contact support if this repeats.",
            );
        }

        const measuredSha256 = normalizedSha256(await digestSha256(bytes));
        assertEpoch(epoch);
        if (!measuredSha256) {
            throw new CloudDocumentStoreError("server", "Document hash calculation failed");
        }
        if (metadata.sha256 && metadata.sha256 !== measuredSha256) {
            // Owner report, 2026-09-02: a drawing sheet's file record can still carry the
            // ORIGINAL upload's sha256 while the bytes actually served at `downloadUrl` are
            // the processed, split single-sheet PDF -- the two never match on a sheet's
            // first-ever open, and rejecting here made every such open fail with no visible
            // reason (owner: "a plain click on a sheet row opens nothing"). The web viewer
            // opens whatever bytes the same URL serves with no sha check at all; matching
            // that, now that `looksLikePdf` above has already confirmed these bytes are a
            // real PDF, is safe -- the cache key two lines down is always the MEASURED hash
            // of what we actually received, never the declared one, so a genuinely corrupt
            // download still can never be served back under a trusted identity.
            console.warn(
                "[cloud-doc] declared sha256 does not match the downloaded PDF -- opening the served bytes under their measured hash",
                {
                    documentId,
                    sourceFileId: options.sourceFileId,
                    declared: metadata.sha256,
                    measured: measuredSha256,
                },
            );
        }

        const persist = dependencies
            .putCached(
                measuredSha256,
                bytes,
                nativeAuthGeneration,
                metadataFileId,
                session.resourceGeneration,
                requestedSha256,
            )
            .then(() => {
                assertEpoch(epoch);
                return dependencies.setLink(
                    measuredSha256,
                    {
                        name,
                        cloudDocId: documentId,
                        ...(options.projectId ? { projectId: options.projectId } : {}),
                    },
                    nativeAuthGeneration,
                    session.resourceGeneration,
                );
            })
            .then(() => assertEpoch(epoch));
        void persist.catch(() => undefined);
        return {
            bytes,
            sha256: measuredSha256,
            name,
            source: "network",
            verifiedSha256: measuredSha256,
        };
    };

    const download = async (
        documentId: string,
        options: CloudDocumentOptions = {},
    ): Promise<CloudDocumentBytes> => {
        throwIfAborted(options.signal);
        // The dedup key carries the CALLER's identity as well as the document's. This map
        // lives for the whole platform lifetime, and `platformEpoch` advances only on an
        // origin change (prod/staging/local-dev), never on a sign-out or an org switch, so
        // a bare `[documentId, sourceFileId]` key would hand a member of two orgs whose
        // databases share a document UUID org A's in-flight bytes straight to the org B
        // renderer. Entity UUIDs collide across tenants (CLAUDE_SHARED.md); `putCached` is
        // already fenced by the captured `nativeAuthGeneration`, and this is the matching
        // fence for the hand-off.
        const identity = await waitWithSignal(
            dependencies.getAuthSession(),
            options.signal,
            abortError,
        );
        throwIfAborted(options.signal);
        const key = JSON.stringify([
            identity?.subjectKey ?? null,
            identity?.generation ?? null,
            identity?.resourceGeneration ?? null,
            documentId,
            options.sourceFileId ?? null,
        ]);
        let shared = inFlightDownloads.get(key);
        if (!shared) {
            shared = downloadOnce(documentId, { ...options, signal: undefined });
            inFlightDownloads.set(key, shared);
            const clear = () => {
                if (inFlightDownloads.get(key) === shared) inFlightDownloads.delete(key);
            };
            void shared.then(clear, clear);
        }
        return waitWithSignal(shared, options.signal, abortError);
    };

    const loadInner = async (
        documentId: string,
        options: CloudDocumentOptions = {},
    ): Promise<CloudDocumentBytes> => {
        throwIfAborted(options.signal);
        const expectedSha256 = normalizedSha256(options.expectedSha256);
        // A file id is the most specific identity a caller has, and the tenant's file-id
        // index answers it locally: one point read, then the bytes under their trusted
        // sha. Asked first so a warm open of a sheet needs no sha from a listing, no
        // sync-mirror row (`sync_files` is empty for most tenants) and no metadata GET.
        if (options.sourceFileId) {
            try {
                const cached = await waitWithSignal(
                    dependencies.getCachedByFileId(options.sourceFileId, expectedSha256),
                    options.signal,
                    abortError,
                );
                if (cached) {
                    return cachedResult(
                        cached,
                        options.expectedName ?? "Project document.pdf",
                        options,
                    );
                }
            } catch (error) {
                if (error instanceof Error && error.name === "AbortError") throw error;
            }
        }
        let sourceSha256: string | undefined;
        if (!expectedSha256 && options.sourceFileId) {
            try {
                const source = await waitWithSignal(
                    dependencies.getSourceFile(options.sourceFileId),
                    options.signal,
                    abortError,
                );
                sourceSha256 = normalizedSha256(source?.sha256);
            } catch (error) {
                if (error instanceof Error && error.name === "AbortError") throw error;
            }
        }
        const knownSha256 = expectedSha256 ?? sourceSha256;
        // A document-level cached link answers "what did we last open for this WHOLE
        // document", mixing every file that document has ever opened under one shared
        // `cloudDocId` identity (`store.rs`'s `working_set_links`, keyed by document
        // alone, not by file). Trusting it is only safe when the caller has no more
        // specific file in mind. A `sourceFileId` (drawing sheet, specification section)
        // must resolve its OWN sha only through the file-id index, `getSourceFile` or a
        // listing's sha above, never a document-level link -- that link may belong to a
        // DIFFERENT file within the same document (the parent drawing set, or a sibling
        // sheet) and would silently serve that file's cached bytes here instead (owner
        // report, 2026-09-02: "we must never open the big PDF of a drawing set").
        let link: WorkingSetLinkRow | undefined;
        if (!options.sourceFileId && (!knownSha256 || !options.expectedName)) {
            try {
                link = latestLinkForDocument(
                    await waitWithSignal(dependencies.getLinks(), options.signal, abortError),
                    documentId,
                );
            } catch (error) {
                if (error instanceof Error && error.name === "AbortError") throw error;
                if (!knownSha256) link = undefined;
            }
        }
        const linkSha256 = normalizedSha256(link?.sha256);
        const sha256 = knownSha256 ?? linkSha256;
        const name =
            options.expectedName ??
            (link && linkSha256 === sha256 ? link.name : undefined) ??
            "Project document.pdf";
        if (sha256) {
            try {
                const cached = await waitWithSignal(
                    dependencies.getCached(sha256),
                    options.signal,
                    abortError,
                );
                if (cached) return cachedResult(cached, name, options);
            } catch (error) {
                if (error instanceof Error && error.name === "AbortError") throw error;
            }
        }
        return download(documentId, options);
    };

    const load = async (
        documentId: string,
        options: CloudDocumentOptions = {},
    ): Promise<CloudDocumentBytes> => {
        try {
            const result = await loadInner(documentId, options);
            return result;
        } catch (error) {
            throw error;
        }
    };

    const getCached = (sha256: string, signal?: AbortSignal) =>
        waitWithSignal(dependencies.getCached(sha256), signal, abortError);
    const getCachedByFileId = (fileId: string, signal?: AbortSignal) =>
        waitWithSignal(dependencies.getCachedByFileId(fileId), signal, abortError);
    const probeCachedByFileId = (fileId: string, signal?: AbortSignal) =>
        waitWithSignal(
            dependencies.probeCachedByFileId?.(fileId) ?? Promise.resolve(null),
            signal,
            abortError,
        );
    const getSourceFile = async (
        fileId: string,
        signal?: AbortSignal,
    ): Promise<CloudSourceFile | null> => {
        if (!nonEmptyString(fileId)) return null;
        const source = await waitWithSignal(dependencies.getSourceFile(fileId), signal, abortError);
        if (!source) return null;
        const sha256 = normalizedSha256(source.sha256);
        const fileSize = normalizedFileSize(source.fileSize);
        return {
            ...(sha256 ? { sha256 } : {}),
            ...(fileSize ? { fileSize } : {}),
        };
    };

    return {
        load,
        download,
        hasCached: dependencies.hasCached,
        getCached,
        getCachedByFileId,
        probeCachedByFileId,
        getSourceFile,
    };
}
